<template>
  <div class="gs-app gv-category-modal">
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <gs-named-modal
      name="create-category-modal"
      :title="$t('frontend_add_category')"
      @closed="closed"
    >
      <div class="gs-app gs-refresh-controls flex flex-col px-8 py-5 gv-category-modal__body">
        <gs-field
          class="gv-category-modal__field"
          :label="$t('frontend_name')"
        >
          <text-input
            v-model="localModel.category"
            :validator="$v.localModel.category"
            :placeholder="$t('frontend_category')"
            :attribute="$t('frontend_groovevideo_categories_category_name')"
          />
        </gs-field>
        <gs-field
          class="gv-category-modal__field"
          :label="$t('frontend_groovevideo_categories_select_parent_category')"
          :hint="
            isParentModel
              ? $t('frontend_groovevideo_categories_subcategory_info')
              : ''
          "
        >
          <treeselect
            v-model="localModel.parent_id"
            class="video-category"
            :multiple="false"
            :clearable="false"
            :disabled="isParentModel"
            :options="availableCategories"
            :max-height="120"
            :placeholder="
              $t('frontend_groovevideo_categories_select_parent_category')
            "
          />
        </gs-field>
        <div class="flex flex-row w-full justify-center pb-6 mb-2">
          <gs-button
            class="text-center h-10 border-grey-1 mr-4"
            size="lg"
            variant="outline"
            @click="closeCategoryModal()"
            >{{ $t('frontend_cancel') }}</gs-button
          >
          <gs-button
            class="text-center h-10"
            size="lg"
            variant="primary"
            @click="saveCategory()"
            >{{ $t('frontend_save') }}</gs-button
          >
        </div>
      </div>
    </gs-named-modal>
  </div>
</template>
<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Loading from 'vue-loading-overlay';
import Treeselect from '@riophae/vue-treeselect';
import Swal from 'sweetalert2';
import Category from '@/models/Category';
import themeConfig from '@/theme/themeConfig.js';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  props: {
    categoryId: {},
    categories: { required },
  },
  components: {
    Loading,
    Treeselect,
  },
  data() {
    return {
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      localModel: {
        id: null,
        category: null,
        parent_id: 0,
      },
      isParentModel: false,
      availableCategories: [],
    };
  },
  async created() {
    this.showLoader();
    await this.resetForm();
    await this.setCategories();
    this.hideLoader();
    this.$root.$emit('modal-open', 'create-category-modal');
  },
  methods: {
    async setCategories() {
      if (!this.isParentModel) {
        this.availableCategories = this.categories.reduce((result, el) => {
          if (
            el.parent_id === 0 &&
            (!this.model.id || el.id !== this.model.id)
          ) {
            result.push({ ...el, label: el.category });
          }
          return result;
        }, []);
      }

      this.availableCategories.unshift({
        id: 0,
        label: this.$t('frontend_groovevideo_categories_no_parent_category'),
      });
    },

    closed() {
      this.$emit('close-modal');
    },
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
    closeCategoryModal() {
      this.$root.$emit('modal-close', 'create-category-modal');
    },
    async resetForm() {
      if (this.categoryId) {
        this.model = await Category.$find(this.categoryId);

        this.localModel.id = this.model.id;
        this.localModel.category = this.model.category;
        this.localModel.parent_id = this.model.parent_id;

        this.isParentModel = this.model.child_count > 0;
      } else {
        this.model = new Category({});
      }
    },
    validate() {
      this.$v.localModel.$touch();
      return !this.$v.localModel.$invalid;
    },
    async saveCategory() {
      const isValid = this.validate();
      if (isValid) {
        this.showLoader();
        this.model.parent_id = this.localModel.parent_id;
        this.model.category = this.localModel.category;
        await this.model
          .save()
          .then((response) => {
            this.$emit('on-save', response.data.id);
          })
          .catch((e) => {
            // Check error message
            let message = 'frontend_backend_error';
            if(e.response && e.response.data && e.response.data.message && e.response.data.message === 'category_heirarchy_level_error') {
              message = 'frontend_groovevideo_categories_subcategory_info';
            }
            Swal.fire(this.$t('frontend_error'), this.$t(message), 'error');
          });

        this.hideLoader();
      }
    },
  },
  validations: {
    localModel: {
      category: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },
};
</script>

<style lang="scss">
.error {
  color: #ea3d5e;
}
.error-class .vue-treeselect div {
  border-color: #ea3d5e !important;
}
.video-category {
  &.vue-treeselect {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
  }
  .vue-treeselect__control {
    border-color: #dcdcea !important;
    border-radius: 6px;
    height: 40px;
    padding: 0 16px;
  }
  .vue-treeselect__control-arrow-container {
    position: relative;
    svg {
      display: none;
    }
    &::after {
      content: '';
      display: inline-block;
      border: solid #dcdcea;
      border-width: 0 2px 2px 0;
      display: inline-block;
      margin-top: -5px;
      padding: 3px;
      transform: rotate(45deg);
    }
  }
  .vue-treeselect__single-value {
    color: #191d3a;
  }
  .vue-treeselect__single-value,
  .vue-treeselect__placeholder {
    padding-left: 8px;
    line-height: 38px;
  }
  .vue-treeselect__menu-container {
    z-index: 9 !important;
    .vue-treeselect__menu {
      width: 100%;
      padding: 0 0;
      border: none;
      border-radius: 6px;
      box-shadow: 20px 20px 40px 0 rgba(22, 29, 37, 0.05);
    }
    .vue-treeselect__list {
      overflow: auto;
      .vue-treeselect__option {
        padding: 8px 16px;
        color: #545671;
        &:hover {
          background-color: #191c3c;
          color: #fff;
        }
      }
      .vue-treeselect__label {
        font-size: 14px;
        font-weight: 600;
      }
    }
  }

  .active .fa-chevron-right {
    transform: rotate(90deg);
  }
}
</style>

<style lang="scss">
.gv-category-modal__body {
  gap: 8px;
  max-width: 42rem;
  margin: 0 auto;
}

.gv-category-modal__body .video-category.vue-treeselect {
  width: 100%;
}

.gv-category-modal__field {
  width: 100%;
  margin-bottom: 12px;
}

@media (max-width: 640px) {
  .gv-category-modal__body {
    padding: 16px !important;
  }

  .gv-category-modal__body > .flex.flex-row {
    align-items: stretch;
    flex-direction: column;
  }

  .gv-category-modal__body > .flex.flex-row .gs-button {
    width: 100%;
  }

  .gv-category-modal__body > .flex.flex-row .mr-4 {
    margin-right: 0 !important;
    margin-bottom: 12px;
  }
}
</style>
